import Vue from "vue";
import Vuex from "vuex";
import storeAxios from "./axios";
import Cookies from "js-cookie";
import { setOrReplaceIn } from './helpers'
import calculator from './modules/calculator';
import skeleton from "@/defaults/skeleton";

Vue.use(Vuex);

export default new Vuex.Store({
    modules: {
        calculator
    },

    state: {
        authProcess: null,
        selectedLang: 'pt',
        storeAxiosLangInterceptor: null,
        user: null,
        usersCalculations: [],
        usersCalculationsList: [],
        usersCalculationsNotes: [],
        calculationData: null,
        hasSeenIntro: Cookies.get("hasSeenIntro"),
        countries: null,
        cookies: null,
    },

    mutations: {
        setAuthProcess(state, { val }) {
            state.authProcess = val;
        },

        setHasSeenIntro(state, { val }) {
            state.hasSeenIntro = val;
        },

        setUser(state, { val }) {
            // val.isInternal = true;
            state.user = val;
        },

        setSelectedLang(state, { val }) {
            state.selectedLang = val;
            Cookies.set("selectedLang", val);
        },

        setStoreAxiosLangInterceptor(state, { val }) {
            state.storeAxiosLangInterceptor = val;
        },

        updateUser(state, { val }) {
            state.user = val;
            Cookies.set("user", JSON.stringify(state.user));
        },

        setCountries(state, { val }) {
            state.countries = val;
        },

        setCalculationData(state, { val }) {
            state.calculationData = val;
        },

        setUsersCalculationsList(state, { val }) {
            state.usersCalculationsList = [...val]
        },

        setUsersCalculationsListItem(state, { val }) {
            setOrReplaceIn(state.usersCalculationsList, val);
        },

        setUsersCalculation(state, { val }) {
            setOrReplaceIn(state.usersCalculations, val)
        },

        setUsersCalculationNote(state, { val }) {
            setOrReplaceIn(state.usersCalculationsNotes, val)
        },

        removeUsersCalculationNote(state, { id }) {
            const idxOfNote = state.usersCalculationsNotes.findIndex(note => id === note.id);

            if (idxOfNote !== -1) {
                state.usersCalculationsNotes.splice(idxOfNote, 1);
            }
        },

        setCookie(state, val ) {
            state.cookies = val;
        },
    },

    actions: {
        initCookie({commit}) {
            const cookies = Cookies.get('cookie');
            if (cookies !== undefined) {
                commit('setCookie', true)
            } else {
                commit('setCookie', false)
            }
        },
        updateCookie({commit}, cookies) {
            Cookies.set("cookie", cookies);
            commit('setCookie', true)
        },
        initAuth({ commit }) {
            const cookiesUser = Cookies.get('user');
            const user = cookiesUser && JSON.parse(cookiesUser);

            let authProcess;

            if (user) {
                authProcess = storeAxios.get("Calculations/summary", {
                    headers: {
                        "Authorization": user.id
                    }
                }).then(() => {
                    storeAxios.defaults.headers.common["Authorization"] = user.id;
                    commit('setUser', { val: user });

                }).catch(() => {
                    Cookies.set('user', null);
                });

            } else {
                authProcess = Promise.resolve()
            }

            commit('setAuthProcess', { val: authProcess });
        },

        login({ commit, dispatch }, { email, password }) {
            return storeAxios
                .post("AppUsers/login", {
                    email,
                    password
                })
                .then(({ data }) => {
                    const user = data;
                    storeAxios.defaults.headers.common["Authorization"] = user.id;
                    Cookies.set("user", JSON.stringify(user));
                    commit("setUser", { val: user })

                    if ('lang' in user) {
                        dispatch('updateSelectedLang', user.lang);
                    }
                })
                .catch(Err => {
                    const error = Err.response.data.error;
                    console.error(error);
                    throw error;
                });
        },

        logout({ commit }) {
            return storeAxios.post("AppUsers/logout")
                .then(() => {
                    storeAxios.defaults.headers.common["Authorization"] = null;
                    Cookies.set("user", null);
                    commit("setUser", { val: null })
                })
                .catch(err => {
                    console.error(err);
                    throw err;
                });
        },

        createAccount({ state, commit, dispatch }, { username, email, password, company, country }) {
            return storeAxios
                .post("AppUsers", {
                    username,
                    email,
                    password,
                    company,
                    country
                },{params:{register_lang: state.selectedLang}})
                .catch(err => {
                    console.error(err);
                    throw err;
                });
        },

        updateAccount({ state, commit, dispatch }, val) {
            commit('updateUser', {
                val: {
                    ...state.user,
                    ...val
                }
            });

            dispatch('updateSelectedLang', val.lang)
        },

        updateSelectedLang({ commit, state }, val) {
            storeAxios.interceptors.request.eject(state.storeAxioslangInterceptor);
            const langInterceptor = storeAxios.interceptors.request.use(config => {
                config.params = config.params || {};
                config.params['lang'] = val;
                return config;
            });

            commit('setSelectedLang', { val })
            commit('setStoreAxiosLangInterceptor', { val: langInterceptor });
        },

        requestPwReset({state},{ email }) {
            return storeAxios
                .post("resetPassword", {
                    email,
                },{params:{register_lang: state.selectedLang}})
                .catch(err => {
                    console.error(err);
                    throw err;
                });
            //{commit}, { email }) {
            //
        },

        setHasSeenIntro({ commit }, val) {
            Cookies.set("hasSeenIntro", "true");
            commit("setHasSeenIntro", { val });
        },

        fetchCalculationData({ state, commit }) {
            if (state.calculationData == null) {
                commit("setCalculationData", { val: skeleton });
            }

            return Promise.resolve(state.calculationData);
        },

        fetchCountries({ state, commit }) {
            if (state.countries !== null) {
                return Promise.resolve(state.countries);
            }

            return storeAxios
                .get("Countries")
                .then(res => {
                    commit("setCountries", { val: res.data });
                })
                .catch(err => {
                    console.error(err);
                    throw err;
                });
        },

        fetchUsersCalculationsList({ commit }) {
            return storeAxios
                .get("Calculations/summary")
                .then(({ data }) => {
                    commit("setUsersCalculationsList", { val: data.data });
                    return data;
                })
                .catch(err => {
                    console.error(err);
                    throw err;
                });
        },

        fetchUsersCalculation({ commit }, id) {
            return storeAxios
                .get(`Calculations/${id}`)
                .then(({ data }) => {
                    commit("setUsersCalculation", { val: data });
                    return data;
                })
                .catch(err => {
                    console.log('fetchUsersCalculation', err)
                    throw err;
                });
        },

        fetchUsersCalculationNote({ commit }, { id, type, lang }) {
            return storeAxios
                .post(`CalculationNotes`, {
                    calculationId: id,
                    type,
                    //lang
                })
                .then(({ data }) => {
                    commit("setUsersCalculationNote", { val: { id, ...data } });
                    return data;
                })
                .catch(err => {
                    console.error(err);
                    throw err;
                });
        },

        createUsersCalculation({ commit }, calculation) {
            return storeAxios
                .post("Calculations", calculation)
                .then(res => {
                    const calculation = res.data;

                    commit("setUsersCalculation", { val: calculation });

                    commit("setUsersCalculationsListItem", {
                        val: {
                            country: calculation.input.location,
                            id: calculation.id,
                            name: calculation.input.Description,
                            spans: calculation.input.modelSpans.length,
                            type: calculation.input.type
                        }
                    });

                    return res.data;
                })
                .catch(err => {
                    const { response } = err;
                    throw response.data.error;
                })

        },

        updateUsersCalculation({ commit }, { id, updates: { Description, chosenSolution } }) {
            return storeAxios
                .patch('Calculations/' + id, {
                    Description,
                    chosenSolution
                })
                .then(() => {
                    commit('removeUsersCalculationNote', { id });
                })
                .catch(err => {
                    console.log('updateUsersCalculation', err)
                })
        }
    },

    getters: {
        sortedUsersCalculationsList(state) {
            return [...state.usersCalculationsList].sort((a, b) => {
                return a.date > b.date ? -1 : a.date < b.date ? 1 : 0;
            });
        },

        loggedInUser(state) {
            return state.user;
        }
    }
});


